/**
 * "Lab Notebook" card for the Feedback card view — a warm cream paper
 * surface with olive accents, a status flag that sticks out at the top-
 * left corner, editorial serif title + monospace ticket number, polaroid
 * style thumbnails with micro tilts, dashed hairline divider, and a
 * lift-off-the-stack hover (translate + tiny rotation + olive glow).
 * Aims for "MoftakiLab lab notebook" feel rather than generic SaaS card.
 */
import { Box, Checkbox, Chip, Paper, Typography } from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { STATUS_COLORS } from './feedbackShared';
import type { FeedbackTicket } from '../../types/feedback';

interface Props {
  ticket: FeedbackTicket;
  showSubmitter?: boolean;
  onClick: () => void;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}

const SEVERITY_COLORS: Record<string, string> = {
  Critical: '#a33b3b', High: '#B8722E', Medium: '#4A6D8C', Low: '#616161',
};

/** Shift a hex color — positive toward black, negative toward white. 0..1 per side. */
function shade(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const blend = amount >= 0
    ? (c: number) => c * (1 - amount)
    : (c: number) => c + (255 - c) * -amount;
  const to = (x: number) => Math.max(0, Math.min(255, Math.round(blend(x)))).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

// Very subtle SVG paper grain as an inline data URL. Tiled across the card
// surface for texture without loading an external asset.
const paperGrain =
  'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22140%22 height=%22140%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22 numOctaves=%222%22 stitchTiles=%22stitch%22/><feColorMatrix values=%220 0 0 0 0.29 0 0 0 0 0.4 0 0 0 0 0.25 0 0 0 0.045 0%22/></filter><rect width=%22140%22 height=%22140%22 filter=%22url(%23n)%22/></svg>")';

export const FeedbackTicketCard = ({ ticket, showSubmitter, onClick, selectable, selected, onToggleSelect }: Props) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;         // olive #4A6741
  const statusColor = STATUS_COLORS[ticket.Status] || '#616161';
  const statusLight = shade(statusColor, -0.12);
  const statusDeep = shade(statusColor, 0.35);
  const sevColor = ticket.Severity ? SEVERITY_COLORS[ticket.Severity] || '#616161' : '#616161';
  const thumbs = ticket.ThumbnailUrls || [];
  const attCount = ticket.AttachmentCount ?? 0;
  const extraAttachments = Math.max(attCount - thumbs.length, 0);

  // Warm olive-tinted layered shadow — feels like the card is resting on cream paper.
  const baseShadow = [
    `0 1px 2px ${alpha(primary, 0.08)}`,
    `0 6px 14px ${alpha(primary, 0.09)}`,
    `0 20px 36px ${alpha(primary, 0.06)}`,
    `inset 0 1px 0 ${alpha('#fff', 0.9)}`,                              // top highlight
    `inset -16px -16px 32px -32px ${alpha('#000', 0.18)}`,              // subtle dog-ear on top-right
  ].join(', ');
  const hoverShadow = [
    `0 4px 10px ${alpha(primary, 0.12)}`,
    `0 16px 32px ${alpha(primary, 0.22)}`,
    `0 36px 60px ${alpha(primary, 0.18)}`,
    `0 0 0 1px ${alpha(primary, 0.28)}`,
    `inset 0 1px 0 ${alpha('#fff', 0.95)}`,
    `inset -16px -16px 32px -32px ${alpha('#000', 0.22)}`,
  ].join(', ');

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        position: 'relative',
        borderRadius: 2.5,
        cursor: 'pointer',
        overflow: 'visible',                               // the status flag extends above the card
        marginTop: '10px',                                 // breathing room for the protruding flag
        border: selected
          ? `1.5px solid ${alpha(primary, 0.6)}`
          : `1.5px solid ${alpha(primary, 0.14)}`,
        borderLeft: `5px solid ${statusColor}`,
        // Cream paper surface — warm radial highlight + olive gradient + noise grain
        backgroundImage: `${paperGrain},
          radial-gradient(ellipse at 20% 0%, ${alpha('#fff', 1)} 0%, transparent 55%),
          linear-gradient(180deg, ${alpha('#FEFCF5', 1)} 0%, ${alpha(primary, 0.05)} 100%)`,
        backgroundColor: '#FEFCF5',
        boxShadow: baseShadow,
        transition: 'transform 0.35s cubic-bezier(0.2, 1, 0.4, 1), box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s ease',
        transformOrigin: '50% 100%',
        '&:hover': {
          transform: 'translateY(-5px) rotate(-0.5deg)',
          boxShadow: hoverShadow,
          borderColor: alpha(primary, selected ? 0.7 : 0.3),
          borderLeftColor: statusColor,
          '& .status-flag': {
            transform: 'translateY(-2px) rotate(-3deg)',
            boxShadow: `0 6px 14px ${alpha(statusColor, 0.55)}, inset 0 1px 0 ${alpha('#fff', 0.35)}`,
          },
        },
      }}>
      {/* Status flag — paper tab sticking above the top-left edge, with a
          subtle triangle "tail" underneath to read as three-dimensional fabric */}
      <Box className="status-flag" sx={{
        position: 'absolute',
        top: -10, left: 16,
        zIndex: 2,
        px: 1.4, pt: 0.6, pb: 0.55,
        color: '#FDF8E8',
        fontSize: '0.63rem',
        fontWeight: 800,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        borderRadius: '3px 3px 6px 6px',
        background: `linear-gradient(135deg, ${statusLight} 0%, ${statusColor} 55%, ${statusDeep} 100%)`,
        boxShadow: `0 3px 8px ${alpha(statusColor, 0.45)}, inset 0 1px 0 ${alpha('#fff', 0.3)}`,
        textShadow: `0 1px 1px ${alpha('#000', 0.35)}`,
        transition: 'transform 0.3s cubic-bezier(0.2, 1, 0.4, 1), box-shadow 0.3s ease',
        // Triangle tail — small folded-paper corner at the bottom-left
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0, bottom: -6,
          width: 0, height: 0,
          borderTop: `6px solid ${statusDeep}`,
          borderRight: '6px solid transparent',
          filter: `drop-shadow(0 1px 1px ${alpha('#000', 0.2)})`,
        },
      }}>
        {ticket.Status}
      </Box>

      {selectable && (
        <Checkbox size="small" checked={!!selected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleSelect?.()}
          sx={{
            position: 'absolute', top: 6, right: 6, zIndex: 3,
            p: 0.5, bgcolor: 'rgba(255,253,240,0.92)', borderRadius: '50%',
            boxShadow: `0 2px 6px ${alpha(primary, 0.2)}, 0 0 0 1px ${alpha(primary, 0.15)}`,
            '&:hover': { bgcolor: '#fff' },
          }} />
      )}

      {/* Body */}
      <Box sx={{ p: 2.25, pt: 2.75, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 160 }}>
        {/* Ticket #, type, severity, admin priority */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.85, flexWrap: 'wrap', mb: 0.25 }}>
          <Typography sx={{
            fontFamily: '"Roboto Mono", Consolas, monospace',
            fontSize: '0.72rem',
            fontWeight: 700,
            color: primary,
            letterSpacing: '0.05em',
          }}>
            {ticket.TicketNumber}
          </Typography>
          <PillChip
            label={ticket.Type === 'bug' ? 'Bug' : 'Feature'}
            bg={ticket.Type === 'bug' ? alpha('#a33b3b', 0.1) : alpha(primary, 0.12)}
            border={ticket.Type === 'bug' ? alpha('#a33b3b', 0.28) : alpha(primary, 0.3)}
            fg={ticket.Type === 'bug' ? '#8a3030' : primary} />
          {(ticket.Severity || ticket.Priority) && (
            <PillChip
              label={ticket.Severity || ticket.Priority || ''}
              bg={alpha(sevColor, 0.1)}
              border={alpha(sevColor, 0.28)}
              fg={sevColor} />
          )}
          {ticket.AdminPriority && (
            <PillChip
              label={ticket.AdminPriority}
              bg={alpha('#B8722E', 0.15)}
              border={alpha('#B8722E', 0.34)}
              fg="#8a5420" />
          )}
        </Box>

        {/* Title in serif — editorial lab-notebook feel */}
        <Typography sx={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontWeight: 600,
          fontSize: '1rem',
          color: '#2d3628',
          lineHeight: 1.35,
          letterSpacing: '-0.005em',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {ticket.RefinedTitle || ticket.Title}
        </Typography>

        {/* Polaroid-style thumbnails inside the card body */}
        {thumbs.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-end', mt: 0.75, mb: 0.25, minHeight: 64 }}>
            {thumbs.slice(0, 3).map((url, i) => {
              const rot = [-1.8, 0, 1.8][i] ?? 0;
              return (
                <Box key={i} sx={{
                  p: 0.4, pb: 0.9,
                  bgcolor: '#fff',
                  boxShadow: `0 2px 6px ${alpha('#000', 0.2)}, 0 0 0 1px ${alpha(primary, 0.1)}`,
                  transform: `rotate(${rot}deg)`,
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  flexShrink: 0,
                  '&:hover': {
                    transform: `rotate(0deg) scale(1.12)`,
                    boxShadow: `0 6px 18px ${alpha('#000', 0.3)}, 0 0 0 1px ${alpha(primary, 0.25)}`,
                    zIndex: 4,
                  },
                }}>
                  <Box component="img" src={url} alt=""
                    sx={{
                      display: 'block',
                      width: 72, height: 50,
                      objectFit: 'cover', objectPosition: 'center',
                      borderRadius: 0.25,
                    }} />
                </Box>
              );
            })}
            {extraAttachments > 0 && (
              <Box sx={{
                alignSelf: 'center',
                px: 1, py: 0.4,
                fontSize: '0.68rem',
                fontWeight: 700,
                color: alpha(primary, 0.75),
                bgcolor: alpha(primary, 0.07),
                border: `1px dashed ${alpha(primary, 0.25)}`,
                borderRadius: 1,
              }}>
                +{extraAttachments}
              </Box>
            )}
          </Box>
        )}

        {/* Spacer pushes footer to the bottom */}
        <Box sx={{ flex: 1 }} />

        {/* Hairline dashed divider — notebook rule feel */}
        <Box sx={{
          borderTop: `1px dashed ${alpha(primary, 0.22)}`,
          mt: 0.75, mb: 0.25,
        }} />

        {/* Footer — submitter + attachment count + date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', pt: 0.25 }}>
          {showSubmitter && (ticket.SubmittedByName || ticket.SubmittedByEmail) && (
            <Typography sx={{
              fontSize: '0.7rem',
              color: alpha(primary, 0.85),
              fontWeight: 600,
              letterSpacing: '0.01em',
            }}>
              {ticket.SubmittedByName || ticket.SubmittedByEmail}
            </Typography>
          )}
          {attCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <AttachFile sx={{ fontSize: 12, color: alpha(primary, 0.6), transform: 'rotate(-35deg)' }} />
              <Typography sx={{ fontSize: '0.7rem', color: alpha(primary, 0.7), fontWeight: 600 }}>
                {attCount}
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography sx={{
            fontSize: '0.68rem',
            fontFamily: '"Roboto Mono", Consolas, monospace',
            color: alpha('#6b6555', 0.8),
            letterSpacing: '0.02em',
          }}>
            {ticket.sysCreateDate ? new Date(ticket.sysCreateDate).toLocaleDateString('en-CA') : ''}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

/** Compact pill chip with inner highlight and subtle drop. */
const PillChip = ({ label, bg, border, fg }: { label: string; bg: string; border: string; fg: string }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      fontSize: '0.6rem',
      height: 18,
      fontWeight: 800,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      borderRadius: 9,
      bgcolor: bg,
      color: fg,
      border: `1px solid ${border}`,
      boxShadow: `0 1px 2px ${alpha('#000', 0.04)}, inset 0 1px 0 ${alpha('#fff', 0.55)}`,
      '& .MuiChip-label': { px: 0.9 },
    }} />
);
